import React, { useState } from 'react';
import { displayError } from '../Config/ApplicationConfig';
import { isHttpAuthenticationError, isHttpResponse } from '../Shared/HttpRequestExecutor';
import { LayoutCentral } from './Layout/LayoutCentral';
import { LoadingError } from './SplashScreens/LoadingError';

type ErrorBoundaryProps = {};

enum ErrorTypes {
    None,
    Normal,
    ChunkLoad,
    AuthenticationError
}

type ErrorBoundaryState = {
    hasError: boolean;
    errorType: ErrorTypes;
    errorMessage: string;
};

export const useError = () => {
    const [_, setError] = useState();

    const throwError = (err: any) => {
        setError(() => {
            throw err;
        });
    };
    return throwError;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, errorMessage: '', errorType: ErrorTypes.None };
    }

    static getDerivedStateFromError(error: Error | Response): Partial<ErrorBoundaryState> {
        if (isHttpResponse(error)) {
            if (isHttpAuthenticationError(error)) {
                return {
                    hasError: true,
                    errorMessage: 'Authentication failure',
                    errorType: ErrorTypes.AuthenticationError
                };
            } else {
                return { hasError: true, errorMessage: error.statusText ?? '', errorType: ErrorTypes.Normal };
            }
        } else if (/.(js|css)\)/g.test(error.message)) {
            return { hasError: true, errorMessage: 'Application outdated', errorType: ErrorTypes.ChunkLoad };
        }

        return { hasError: true, errorMessage: error.message ?? '', errorType: ErrorTypes.Normal };
    }

    componentDidCatch() {
        if (this.state.errorType === ErrorTypes.Normal)
            if (this.state.errorMessage) displayError(this.state.errorMessage);
    }

    render() {
        if (this.state.hasError) {
            if (this.state.errorType === ErrorTypes.ChunkLoad) {
                return (
                    <LayoutCentral>
                        <LoadingError message='The application has been updated.' />
                    </LayoutCentral>
                );
            } else if (this.state.errorType === ErrorTypes.AuthenticationError) {
                return (
                    <LayoutCentral>
                        <LoadingError
                            message='Permission error occurred.'
                            info='Please reload the page to update permissions.'
                        />
                    </LayoutCentral>
                );
            } else {
                return (
                    <LayoutCentral>
                        <LoadingError />
                    </LayoutCentral>
                );
            }
        }
        return this.props.children;
    }
}
